import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { PropertyValuationData } from '../types/valuation';
import { getPropertyValuation } from '../api/valuation';
import ErrorBoundary from '../components/error/ErrorBoundary';
import { PropertySqueezeOverlay } from '../components/property/valuation/PropertySqueezeOverlay';
import {
  PropertyHeader,
  QuickStats,
  AnalysisSection,
  AdditionalAnalysis,
  ComparableProperties
} from '../components/property/valuation/components';

const transformValue = (value: any): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[^0-9.-]+/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const DebugValuationContent: React.FC = () => {
  const { listingId = '147669272' } = useParams<{ listingId?: string }>();
  const { config } = useTenant();

  const [selectedSection, setSelectedSection] = useState<string>('all');
  const [propertyData, setPropertyData] = useState<PropertyValuationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  const [customListingId, setCustomListingId] = useState(listingId);

  const colors = config?.branding?.colors || {
    primary: '#000000',
    secondary: '#ffffff',
    accent: '#28a745',
    background: '#ffffff',
    text: '#202124'
  };

  const loadData = async () => {
    if (!customListingId) {
      setError('Listing ID is required');
      return;
    }

    try {
      setError(null);
      const data = await getPropertyValuation(customListingId);
      setPropertyData(data);
      console.log('Loaded property data:', data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    }
  };

  const metrics = useMemo(() => {
    if (!propertyData) {
      return {
        valueChangePercent: '0',
        avgCompPricePerSqft: 0,
        avgDaysOnMarket: 0,
        totalComps: 0,
        recentSales: 0
      };
    }

    // Get comps with valid prices and square footage
    const validComps = (propertyData.comps || []).filter(comp => {
      const price = comp.mlsSoldPrice || transformValue(comp.estimatedValue);
      const sqft = transformValue(comp.squareFeet);
      return price > 0 && sqft > 0;
    });

    // Calculate average price per square foot
    const avgCompPricePerSqft = validComps.length > 0
      ? validComps.reduce((sum, comp) => {
          const price = comp.mlsSoldPrice || transformValue(comp.estimatedValue);
          const sqft = transformValue(comp.squareFeet);
          return sum + (price / sqft);
        }, 0) / validComps.length
      : 0;

    // Calculate average days on market
    const validDaysOnMarket = validComps
      .map(comp => transformValue(comp.mlsDaysOnMarket))
      .filter(days => days > 0);
    const avgDaysOnMarket = validDaysOnMarket.length > 0
      ? Math.round(validDaysOnMarket.reduce((a, b) => a + b, 0) / validDaysOnMarket.length)
      : 0;

    // Calculate value change percent
    const currentValue = transformValue(propertyData.estimatedValue);
    const lastSalePrice = transformValue(propertyData.lastSalePrice);
    const valueChangePercent = lastSalePrice > 0
      ? ((currentValue - lastSalePrice) / lastSalePrice * 100).toFixed(1)
      : '0';

    return {
      valueChangePercent,
      avgCompPricePerSqft,
      avgDaysOnMarket,
      totalComps: propertyData.comps?.length || 0,
      recentSales: validComps.length
    };
  }, [propertyData]);

  const capRate = useMemo(() => {
    if (!propertyData?.demographics?.suggestedRent || !propertyData?.estimatedValue) {
      return '0';
    }

    const annualRent = transformValue(propertyData.demographics.suggestedRent) * 12;
    const effectiveGrossIncome = annualRent * 0.85; // Assuming 15% vacancy/expenses
    const propertyValue = transformValue(propertyData.estimatedValue);
    
    return ((effectiveGrossIncome / propertyValue) * 100).toFixed(1);
  }, [propertyData]);

  const renderDebugControls = () => (
    <div className="bg-gray-100 p-4 mb-6 rounded-lg">
      <h2 className="text-lg font-semibold mb-4">Debug Controls</h2>
      <div className="space-y-4">
        <div>
          <label className="block mb-2">Section to Display:</label>
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="all">All Sections</option>
            <option value="header">Property Header</option>
            <option value="quickStats">Quick Stats</option>
            <option value="analysis">Analysis Section</option>
            <option value="additional">Additional Analysis</option>
            <option value="comps">Comparable Properties</option>
          </select>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block mb-2">Listing ID:</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={customListingId}
                onChange={(e) => setCustomListingId(e.target.value)}
                className="flex-1 p-2 border rounded"
                placeholder="Enter listing ID"
              />
              <button
                onClick={loadData}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Load Data
              </button>
            </div>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setPropertyData(null)}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Clear Data
            </button>
            <button
              onClick={() => setShowOverlay(!showOverlay)}
              className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
            >
              {showOverlay ? 'Hide Overlay' : 'Show Overlay'}
            </button>
            <button
              onClick={() => setShowRawData(!showRawData)}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              {showRawData ? 'Hide Raw Data' : 'Show Raw Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRawData = () => {
    if (!showRawData || !propertyData) return null;

    return (
      <div className="bg-gray-100 p-4 mb-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">Raw Property Data</h3>
        <pre className="bg-white p-4 rounded overflow-auto max-h-96">
          {JSON.stringify(propertyData, null, 2)}
        </pre>
      </div>
    );
  };

  const renderSection = () => {
    if (!propertyData) {
      return (
        <div className="text-center p-8 bg-gray-50 rounded">
          <p className="text-gray-600">No data loaded. Use the controls above to load data.</p>
        </div>
      );
    }

    const content = (() => {
      switch (selectedSection) {
        case 'header':
          return (
            <PropertyHeader
              propertyData={propertyData}
              colors={colors}
              metrics={metrics}
              onNavigateBack={() => {}}
            />
          );
        case 'quickStats':
          return (
            <QuickStats
              propertyData={propertyData}
              colors={colors}
              metrics={metrics}
              capRate={capRate}
            />
          );
        case 'analysis':
          return (
            <AnalysisSection
              propertyData={propertyData}
              transformValue={transformValue}
            />
          );
        case 'additional':
          return (
            <AdditionalAnalysis
              propertyData={propertyData}
              transformValue={transformValue}
            />
          );
        case 'comps':
          return (
            <ComparableProperties
              propertyData={propertyData}
              colors={colors}
              transformValue={transformValue}
            />
          );
        default:
          return (
            <>
              <PropertyHeader
                propertyData={propertyData}
                colors={colors}
                metrics={metrics}
                onNavigateBack={() => {}}
              />
              <div className="container mx-auto px-4 py-8">
                <QuickStats
                  propertyData={propertyData}
                  colors={colors}
                  metrics={metrics}
                  capRate={capRate}
                />
                <AnalysisSection
                  propertyData={propertyData}
                  transformValue={transformValue}
                />
                <AdditionalAnalysis
                  propertyData={propertyData}
                  transformValue={transformValue}
                />
                <ComparableProperties
                  propertyData={propertyData}
                  colors={colors}
                  transformValue={transformValue}
                />
              </div>
            </>
          );
      }
    })();

    return (
      <div className="relative">
        {content}
        {showOverlay && propertyData && (
          <PropertySqueezeOverlay
            propertyDetails={propertyData}
            onClose={() => setShowOverlay(false)}
            onSuccess={() => {
              setShowOverlay(false);
              console.log('Overlay success');
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Valuation Page Debug</h1>
      {renderDebugControls()}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      {renderRawData()}
      <div className="bg-white rounded-lg shadow">
        {renderSection()}
      </div>
    </div>
  );
};

const DebugValuationPage: React.FC = () => {
  return (
    <ErrorBoundary>
      <DebugValuationContent />
    </ErrorBoundary>
  );
};

export default DebugValuationPage;
